// iter 159: contract editor dialog. Currently exposes only the user-editable
// PO shipping date (delivery-from-port to customer warehouse). All other
// contract fields come from FileMaker and are read-only.
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { api } from "../lib/api";

export function ContractDialog({ open, onOpenChange, contractNo, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contract, setContract] = useState(null);
  const [poShippingDate, setPoShippingDate] = useState("");

  useEffect(() => {
    if (!open || !contractNo) return;
    setLoading(true);
    setContract(null);
    api.get(`/contracts/${encodeURIComponent(contractNo)}`)
      .then(({ data }) => {
        setContract(data);
        setPoShippingDate(data.po_shipping_date || "");
      })
      .catch((e) => {
        toast.error(`Failed to load contract: ${e.response?.data?.detail || e.message}`);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, contractNo, onOpenChange]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/contracts/${encodeURIComponent(contractNo)}`, {
        po_shipping_date: poShippingDate || null,
      });
      toast.success(`Saved. Updated ${data.variants_updated} variant row(s).`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle>Contract · {contractNo}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
        ) : contract ? (
          <div className="space-y-3 py-2 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1 text-gray-500">Customer</div>
              <div className="col-span-2 font-medium text-gray-800">{contract.customer_name || "-"}</div>
              <div className="col-span-1 text-gray-500">Budget No.</div>
              <div className="col-span-2 font-mono text-gray-700">{contract.budget_no || "-"}</div>
              <div className="col-span-1 text-gray-500">PO No.</div>
              <div className="col-span-2 font-mono text-gray-700">{contract.po_number || "-"}</div>
              <div className="col-span-1 text-gray-500">Working Date</div>
              <div className="col-span-2 font-mono text-gray-700">{contract.contract_working_date || "-"}</div>
              <div className="col-span-1 text-gray-500">Delivery Date</div>
              <div className="col-span-2 font-mono text-gray-700">{contract.delivery_date || "-"}</div>
            </div>
            <div className="border-t border-gray-200 pt-3">
              <Label htmlFor="po_shipping_date" className="text-xs font-medium text-gray-700">
                PO Shipping Date <span className="text-gray-400 font-normal">(shipped to customer's warehouse after arrival at port)</span>
              </Label>
              <Input
                id="po_shipping_date"
                data-testid="contract-po-shipping-date-input"
                type="date"
                value={poShippingDate}
                onChange={(e) => setPoShippingDate(e.target.value)}
                className="mt-1.5 font-mono"
              />
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="contract-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="contract-dialog-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
